import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { UseMutateFunction } from '@tanstack/react-query';

import { emailValidRegex } from '../regex';
import { getBrowserInfo, getUserAgent } from '../getUserAgent';
import { Nullable } from '../../../types/helper';
import Loading from '../../@common/Loading';
import { getViewerHistoryStorageKey } from '../../../utils/viewerHistoryStorage';
import { useViewerRoute } from '../../../contexts/ViewerRoute';

const UTM_MAPPING: Record<string, string> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
  utm_id: 'utmId',
  gclid: 'gclid',
  fbclid: 'fbclid',
  msclkid: 'msclkid',
  ttclid: 'ttclid',
  li_fat_id: 'liFatId',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  gad_source: 'gadSource',
};

function extractCampaignParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const result: Record<string, string> = {};
  for (const [urlKey, bodyKey] of Object.entries(UTM_MAPPING)) {
    const value = params.get(urlKey);
    if (value) {
      result[bodyKey] = value;
    }
  }
  return result;
}

interface ShareFileHashIdPageProps {
  name: string;
  thumnail: string;
  ipAddress: string;
  isDeleted?: boolean;
}

type FileActionRequest = {
  hashId: string;
  deviceInfo: string;
  ipAddress: string;
  browserInfo: string;
  sharedFrom?: string;
  referrer?: string;
  email: Nullable<string>;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  utmId?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  ttclid?: string;
  liFatId?: string;
  gbraid?: string;
  wbraid?: string;
  gadSource?: string;
};

type BulkFileActionRequest = {
  hash: string;
  email?: string;
  deviceInfo: string;
  ipAddress: string;
  browserInfo: string;
  sharedFrom?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  utmId?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  ttclid?: string;
  liFatId?: string;
  gbraid?: string;
  wbraid?: string;
  gadSource?: string;
};
interface Props<T, U, V> extends ShareFileHashIdPageProps {
  mutateBulk?: UseMutateFunction<T, U, BulkFileActionRequest, V>;
  mutateOneToOne?: UseMutateFunction<T, U, Omit<FileActionRequest, 'email'>, V>;
  translateKoToEn: (item: string) => string;
}

const Enter = <T, U, V>({
  name,
  thumnail,
  isDeleted = false,
  ipAddress = '0.0.0.0',
  mutateBulk,
  mutateOneToOne,
  translateKoToEn,
}: Props<T, U, V>) => {
  const router = useRouter();
  const { hashId, resumeViewer } = useViewerRoute();
  const [alertModalOpen, setAlertModalOpen] = useState(isDeleted);

  //enter시 공유된 열람으로 부터 온 링크인지 여부를 sh query로 판단한다
  const sharedHistoryId = router.query.sh;

  const onError = (err: unknown) => {
    if (axios.isAxiosError(err) && err.response?.data) {
      setAlertModalOpen(true);
    }
  };
  const [isKakaoInAppBrowser, setIsKakaoInAppBrowser] = useState(false);

  useEffect(() => {
    if (isKakaoInAppBrowser) {
      return;
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const target_url = location.href;
    const referrer = document.referrer || undefined;

    if (userAgent.match(/kakaotalk/i)) {
      setIsKakaoInAppBrowser(true);
      location.href =
        'kakaotalk://web/openExternal?url=' + encodeURIComponent(target_url);
      return;
    }

    if (!hashId) {
      setAlertModalOpen(true);
      return;
    }

    const savedHistoryId = sessionStorage.getItem(
      getViewerHistoryStorageKey({ hashId, isBulk: Boolean(mutateBulk) }),
    );
    if (savedHistoryId) {
      resumeViewer(savedHistoryId);
      return;
    }

    if (hashId) {
      if (mutateBulk) {
        const email = router.query.email as string;
        const isValidEmail = emailValidRegex.test(email);

        mutateBulk(
          {
            hash: hashId,
            deviceInfo: getUserAgent(navigator.userAgent),
            ipAddress,
            browserInfo: getBrowserInfo(navigator.userAgent).name,
            email: isValidEmail ? email : undefined,
            sharedFrom:
              typeof sharedHistoryId === 'string' ? sharedHistoryId : undefined,
            referrer,
            ...extractCampaignParams(),
          },
          {
            onError,
          },
        );
        return;
      } else if (mutateOneToOne) {
        mutateOneToOne(
          {
            hashId,
            deviceInfo: getUserAgent(navigator.userAgent),
            ipAddress,
            browserInfo: getBrowserInfo(navigator.userAgent).name,
            sharedFrom:
              typeof sharedHistoryId === 'string' ? sharedHistoryId : undefined,
            referrer,
            ...extractCampaignParams(),
          },
          {
            onError,
          },
        );
      }
    } else {
      setAlertModalOpen(true);
    }
  }, [router, hashId, isKakaoInAppBrowser]);

  return (
    <Loading
      alertModalOpen={alertModalOpen}
      setAlertModalOpen={setAlertModalOpen}
      name={name}
      thumnail={thumnail}
      translateKoToEn={translateKoToEn}
      isKakaoInAppBrowser={isKakaoInAppBrowser}
    />
  );
};

export default Enter;
